/* global listUsersUrl uncommittedExpendituresCreateUrl uncommittedExpendituresDetailsUrl uncommittedExpendituresStoreUrl committedExpendituresCreateUrl */
/* global committedExpendituresIndexUrl committedExpendituresStoreUrl uncommittedExpendituresUpdateUrl expenseFundApprovalsUpdateUrl suppliersIndexUrl */
/* global ACCEPTED_ATTACHMENT_FILE_TYPES suppliersStoreUrl suppliersCreateUrl */

import React, { useState, useEffect, useRef, useMemo } from 'react';

import classNames from 'classnames';
import { Modal } from 'flowbite';
import { Formik, Field } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';

import MilestonesEdit from '../milestones/procurement_milestones/MilestonesEdit';
import DangerAlert from '../partial/alerts/DangerAlert';
import ProcurementExpensesDetailedTable from '../procurement_plan/ProcurementExpensesDetailedTable';
import RequestSupplierModal from '../procurement_plan/RequestSupplierModal';
import PurchaseRequisitionProcurementMethodModal from '../purchase_requisitions/PurchaseRequisitionProcurementMethodModal';
import SuppliersCreateModal from '../suppliers/SuppliersCreateModal';

import PurchaseOrdersCreateCommittedExpendituresCreate from './PurchaseOrdersCreateCommittedExpendituresCreate';
import PurchaseOrdersCreateUncommittedExpenditureCreate from './PurchaseOrdersCreateUncommittedExpenditureCreate';

import { idempotencyHeader } from 'Helpers/apiUtils';
import { generateKey } from 'Helpers/misc';
import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import CheckboxFormField from 'CommonComponents/CheckboxFormField';
import DatePickerField from 'CommonComponents/DatePickerField';
import SelectField from 'CommonComponents/SelectField';
import Tooltip from 'CommonComponents/Tooltip';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import { useToast } from 'Hooks/useToast';

import InformationCircleIcon from 'Assets/icons/information-circle.svg';
import PlusCircleIcon from 'Assets/icons/plus-circle.svg';

const OrderCreateSchema = Yup.object().shape({
    owner_id: Yup.string().required($t('This value is required')),
    description: Yup.string(),
    overview: Yup.string(),
    responses: Yup.string(),
    procurement_policy_id: Yup.string().required($t('This value is required')),
    supplier_id: Yup.string().required($t('This value is required')),
    contract_id: Yup.string(),
    contract_started_at: Yup.string(),
    contract_ended_at: Yup.string(),
    contract_closed: Yup.boolean(),
    contract_signed: Yup.boolean(),
    conflicts: Yup.string(),
    risks: Yup.string(),
    other: Yup.string(),
});

export default function PurchaseOrdersCreateForm(props) {
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [procurementPolicies, setProcurementPolicies] = useState([]);
    const [purchaseRequisition, setPurchaseRequisition] = useState({});
    const [user, setUser] = useState({});
    const [submissionDate, setSubmissionDate] = useState('');
    const [suppliers, setSuppliers] = useState([]);

    const [committedExpenditures, setCommittedExpenditures] = useState([]);
    // Resume a saved draft (IB-610): when the server passes an existing draft group id, mount the
    // wizard against that group and skip the budget-selection step so its saved line items load
    // straight into the "Proceed to Commit" step (step 2), matching DC's full-resume behaviour.
    // A fresh EA create has no resumeGroupId — the form creates its own group on first selection.
    const [uncommittedExpenditureGroup, setUncommittedExpenditureGroup] = useState(props.resumeGroupId ? { id: props.resumeGroupId } : null);

    const [lineItems, setLineItems] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [financialStep, setFinancialStep] = useState(props.resumeGroupId ? 2 : 1);
    // Milestone gating mirrors the Direct Commit form (the proven path). The EA runs milestones in
    // direct-commit mode, which reports per-section percentage validity + autosave state via
    // callbacks — NOT the legacy "confirmed" flags (those were only set by a Confirm button that
    // direct-commit mode doesn't render, so the old gate could never satisfy). IB-610.
    const [sectionValidity, setSectionValidity] = useState({});
    const [presentSections, setPresentSections] = useState([]);
    const [milestoneSaving, setMilestoneSaving] = useState({});
    const [uncommittedTotal, setUncommittedTotal] = useState(0);
    const [milestonesEditKey, setMilestonesEditKey] = useState(0);
    const prevFinancialStepRef = useRef(financialStep);
    const [total, setTotal] = useState(0);
    const [variance, setVariance] = useState(0);
    const [maxQuantities, setMaxQuantities] = useState(1);

    const [createNewSupplier, setCreateNewSupplier] = useState(false);
    const [expId, setExpId] = useState(null);
    const [error, setError] = useState(null);

    const [suppliersCreateModalElement, setSuppliersCreateModalElement] = useState(null);

    // Supplier-request modal + awaiting state for ERP-connected events (IB-610).
    const [requestSupplierModalElement, setRequestSupplierModalElement] = useState(null);
    const requestSupplierModalRef = useRef();
    // Seed from the server payload so the "Awaiting supplier" banner + submit-block survive a
    // reload when an open request is held on the draft group, mirroring DC (IB-610 R4).
    const [supplierRequest, setSupplierRequest] = useState(props.existingSupplierRequest || null);
    const [resolvingRequest, setResolvingRequest] = useState(false);
    const supplierRequestRecipients = props.supplierRequestRecipients || [];
    const hasRecipients = supplierRequestRecipients.length > 0;

    const toast = useToast();

    // Finding #1 — the requestor found the supplier already exists and picked it from the
    // dropdown while the draft is still awaiting. Withdraw the open request: links the chosen
    // supplier, clears the awaiting banner so submit unblocks, and emails the configured
    // recipients a "no longer required" follow-up so AP skip the duplicate vendor.
    const buildWithdrawUrl = () =>
        props.supplierRequestWithdrawUrl && supplierRequest ? props.supplierRequestWithdrawUrl.replace('SUPPLIER_REQUEST_ID', supplierRequest.id) : '';

    const handleWithdrawForSupplier = (supplierId) => {
        if (!supplierRequest || !supplierId || !props.supplierRequestWithdrawUrl) {
            return;
        }
        setResolvingRequest(true);
        axios
            .post(buildWithdrawUrl(), { supplier_id: supplierId }, { headers: { ...idempotencyHeader() } })
            .then(() => {
                setSupplierRequest(null);
                toast.success($t('Supplier selected — request withdrawn'));
            })
            .catch(() => toast.error($t('An error occurred, please try again')))
            .finally(() => setResolvingRequest(false));
    };

    // Tracks the latest Formik values so the out-of-tree supplier-request modal can persist
    // the current draft before sending the request (IB-610).
    const latestValuesRef = useRef(null);
    // Lets the request modal set the supplier field when the user picks an existing supplier from
    // the type-ahead suggestions instead of requesting a new one (IB-610 stretch).
    const setFieldValueRef = useRef(null);

    // Persist the EA draft metadata (IB-610). Does NOT submit for approval — usable while a
    // supplier request is open. Returns the axios promise so the request modal can chain off it.
    const handleSaveDraft = (values, { silent = false } = {}) => {
        if (!props.draftSaveUrl || !uncommittedExpenditureGroup) {
            return Promise.resolve();
        }

        setError(null);

        const payload = {
            uncommittedExpenditureGroupId: uncommittedExpenditureGroup.id,
            owner_id: values?.owner_id ?? null,
            description: values?.description ?? null,
            supplier_id: values?.supplier_id || null,
            contract_id: values?.contract_id || null,
            contract_started_at: values?.contract_started_at ? moment(values.contract_started_at).format('YYYY-MM-DD') : null,
            contract_ended_at: values?.contract_ended_at ? moment(values.contract_ended_at).format('YYYY-MM-DD') : null,
            contract_signed: values?.contract_signed ? true : false,
            contract_closed: values?.contract_closed ? true : false,
        };

        return axios
            .post(props.draftSaveUrl, payload, { headers: { ...idempotencyHeader() } })
            .then((response) => {
                if (!silent) {
                    toast.success($t('Draft saved'));
                }
                return response;
            })
            .catch((e) => {
                console.log(e);
                setError(e?.response?.data?.errors);
                throw e;
            });
    };

    const suppliersOptions = useMemo(() => {
        return suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name }));
    }, [suppliers]);

    const suppliersCreateModalRef = useRef();

    const financialRef = useRef(null);

    useEffect(() => {
        if (createNewSupplier) {
            suppliersCreateModalElement && suppliersCreateModalElement.show();
        }
    }, [createNewSupplier]);

    // Rerender MilestonesEdit when financialStep decreases from 3 to 2
    useEffect(() => {
        const financialStepDecreased = prevFinancialStepRef.current === 3 && financialStep === 2;

        if (financialStepDecreased) {
            setMilestonesEditKey((prev) => prev + 1);
        }

        prevFinancialStepRef.current = financialStep;
    }, [financialStep]);

    const closeModal = () => {
        setCreateNewSupplier(false);
        suppliersCreateModalElement && suppliersCreateModalElement.hide();
    };

    const successHandler = () => {
        fetchData();
        setCreateNewSupplier(false);
        suppliersCreateModalElement && suppliersCreateModalElement.hide();
    };

    useEffect(() => {
        fetchData();
        setSubmissionDate(moment().format('DD/MM/YYYY'));
        suppliersCreateModalRef && setSuppliersCreateModalElement(new Modal(suppliersCreateModalRef.current, modalOptions(false)));
        requestSupplierModalRef && setRequestSupplierModalElement(new Modal(requestSupplierModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        let lineItemsTotal = 0;
        let lineItemsVariance = 0;

        lineItems.forEach((item) => {
            if (item.expense?.type === 'pack') {
                lineItemsTotal += item.amount_data.total || item.cashflow_object.total;
                lineItemsVariance = getVariance(item, lineItemsVariance);
            } else {
                if (item.expense?.type === 'project') {
                    item.uncommitted_expenditures.forEach((childItem) => {
                        lineItemsTotal += childItem.amount_data.total || childItem.cashflow_object.total;
                        if (childItem.expense?.type === 'pack') {
                            lineItemsVariance = getVariance(childItem, lineItemsVariance);
                        } else {
                            // For all other types of expenses if new total is not equal to old total then add difference of new and old total into variance
                            if (childItem.originalCashflowObject?.uncommitted != childItem.cashflow_object?.uncommitted) {
                                lineItemsVariance = getVariance(childItem, lineItemsVariance);
                            }
                        }
                    });
                } else {
                    lineItemsTotal += item.amount_data.total || item.cashflow_object.total;
                    // For all other types of expenses if new total is not equal to old total then add difference of new and old total into variance
                    if (item.originalCashflowObject?.uncommitted != item.cashflow_object?.uncommitted) {
                        lineItemsVariance = getVariance(item, lineItemsVariance);
                    }
                }
            }
        });

        setTotal(lineItemsTotal);
        setVariance(lineItemsVariance);
        setUncommittedTotal(lineItemsTotal);
    }, [lineItems]);

    // Lifted from each milestone section (direct-commit mode). All present sections must total
    // 100%, and no section may have an autosave in flight — identical to the Direct Commit gate.
    const handleValidityChange = (section, isValid) => {
        setSectionValidity((prev) => (prev[section] === isValid ? prev : { ...prev, [section]: isValid }));
    };

    const handleSectionPresent = (section) => {
        setPresentSections((prev) => (prev.includes(section) ? prev : [...prev, section]));
    };

    const handleMilestoneSavingChange = (section, isSaving) => {
        setMilestoneSaving((prev) => (prev[section] === isSaving ? prev : { ...prev, [section]: isSaving }));
    };

    const milestonesValid = presentSections.length === 0 || presentSections.every((section) => sectionValidity[section] === true);
    const milestoneSaveInFlight = presentSections.some((section) => milestoneSaving[section] === true);

    useEffect(() => {
        const res = financialStep === 3 && total !== 0 && milestonesValid && !milestoneSaveInFlight;
        setSubmitDisabled(!res);
    }, [total, financialStep, milestonesValid, milestoneSaveInFlight]);

    const fetchData = () => {
        axios
            .get(props.refreshCommitmentDataUrl)
            .then((response) => {
                const { data } = response;
                setUser({ ...data.user });
                setPurchaseRequisition({ ...data.purchaseRequisition });
                setSuppliers([...data.suppliers]);
                setProcurementPolicies([...data.procurementPolicies]);
            })
            .catch((e) => {
                console.log(e);
            });
    };

    const getVariance = (item, lineItemsVariance) => {
        let oldTotal = 0;
        if (item.expense?.type === 'pack') {
            oldTotal = item.expense.cashflowObject?.uncommitted ?? item.expense.cashflowObject?.total ?? 0;
        } else {
            oldTotal = item.originalCashflowObject?.uncommitted ?? item.originalCashflowObject?.total ?? 0;
        }

        let newTotal = item.cashflow_object.total;

        // Add difference of new and old total (prioritizing uncommitted amount) into variance
        lineItemsVariance += newTotal - oldTotal;

        return lineItemsVariance;
    };

    const changeFilesHandler = (event) => {
        setSelectedFiles(event.target.files);
    };

    const changeFinancialStep = (step) => {
        // Stepping back below the final step disables submit via the financialStep gate; the
        // milestone section remounts (milestonesEditKey) and re-reports its validity, so no manual
        // reset of milestone state is needed here (IB-610 — validity-callback gating).
        financialRef.current.scrollIntoView();
        setFinancialStep(step);
    };

    // Purchase Requisition Procurement Method Modal Element

    const [purchaseRequisitionProcurementMethodModalElement, setPurchaseRequisitionProcurementMethodModalElement] = useState(null);

    const purchaseRequisitionProcurementMethodModalRef = useRef();

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    useEffect(() => {
        purchaseRequisitionProcurementMethodModalRef &&
            setPurchaseRequisitionProcurementMethodModalElement(new Modal(purchaseRequisitionProcurementMethodModalRef.current, modalOptions(false)));
    }, []);

    const resetData = () => {
        purchaseRequisitionProcurementMethodModalElement && purchaseRequisitionProcurementMethodModalElement.hide();
    };

    const closePMModal = () => {
        resetData();
    };

    const successPMHandler = () => {
        resetData();
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const showPurchaseRequisitionProcurementMethodModal = () => {
        purchaseRequisitionProcurementMethodModalElement && purchaseRequisitionProcurementMethodModalElement.show();
    };

    const formatContractOptionLabel = (option, { context }) => {
        if (context === 'menu' && option.tooltip) {
            return (
                <Tooltip
                    allowTooltip
                    placement="right"
                    tooltipType="text"
                    offset={[0, 18]}
                    tooltipContent={
                        <div className="tw-text-xs tw-space-y-1">
                            <div>
                                <span className="tw-font-semibold">{$t('Supplier Name')}:</span> {option.tooltip.supplierName}
                            </div>
                            <div>
                                <span className="tw-font-semibold">{$t('Department')}:</span> {option.tooltip.department || '-'}
                            </div>
                            <div>
                                <span className="tw-font-semibold">{$t('Contract Value')}:</span> {option.tooltip.contractValue}
                            </div>
                            <div>
                                <span className="tw-font-semibold">{$t('Actuals')}:</span> {option.tooltip.actuals}
                            </div>
                            {!!option.tooltip.variance && (
                                <div>
                                    <span className="tw-font-semibold">{$t('Variance')}:</span> {option.tooltip.variance}
                                </div>
                            )}
                        </div>
                    }
                >
                    <div>{option.label}</div>
                </Tooltip>
            );
        }
        return option.label;
    };

    return (
        <div>
            {!!Object.keys(purchaseRequisition).length && (
                <Formik
                    initialValues={{
                        // Rehydrate from a previously saved draft (IB-610) when present.
                        owner_id: props.draftData?.owner_id ?? purchaseRequisition.procurement_plan_item?.owner_id ?? '',
                        description: props.draftData?.description ?? purchaseRequisition.procurement_plan_item?.description ?? '',
                        overview: '',
                        responses: '',
                        procurement_policy_id: purchaseRequisition.procurement_plan_item?.policy_id || '',
                        supplier_id: props.groupSupplierId ?? '',
                        contract_id: props.draftData?.contract_id ?? '',
                        contract_started_at: props.draftData?.contract_started_at ?? '',
                        contract_ended_at: props.draftData?.contract_ended_at ?? '',
                        contract_closed: props.draftData?.contract_closed ?? false,
                        contract_signed: props.draftData?.contract_signed ?? false,
                        conflicts: '',
                        risks: '',
                        other: '',
                    }}
                    validationSchema={OrderCreateSchema}
                    onSubmit={(values, actions) => {
                        let data = { ...values };
                        setError(null);

                        const formData = new FormData();
                        formData.append('owner_id', data.owner_id);
                        formData.append('description', data.description);
                        formData.append('final_price', '' + total);
                        formData.append('variance', '' + variance);
                        formData.append('lineItems', JSON.stringify(lineItems));
                        formData.append('uncommittedExpenditureGroupId', uncommittedExpenditureGroup ? uncommittedExpenditureGroup.id : null);
                        formData.append('overview', data.overview);
                        formData.append('procurement_policy_id', data.procurement_policy_id);
                        formData.append('responses', data.responses);
                        formData.append('supplier_id', data.supplier_id);
                        if (data.contract_id) {
                            formData.append('contract_id', data.contract_id);
                        }
                        formData.append('contract_started_at', data.contract_started_at ? moment(data.contract_started_at).format('YYYY-MM-DD') : '');
                        formData.append('contract_ended_at', data.contract_ended_at ? moment(data.contract_ended_at).format('YYYY-MM-DD') : '');
                        formData.append('contract_signed', data.contract_signed ? '1' : '0');
                        formData.append('contract_closed', data.contract_closed ? '1' : '0');
                        formData.append('conflicts', data.conflicts);
                        formData.append('risks', data.risks);
                        formData.append('other', data.other);

                        for (let i = 0; i < selectedFiles.length; i++) {
                            formData.append(`files[${i}]`, selectedFiles[i]);
                        }

                        axios
                            .post(props.storeUrl, formData, {
                                headers: { ...props.mutationHeader },
                            })
                            .then((res) => {
                                window.location.href = props.procurementsDashboardUrl;
                            })
                            .catch((e) => {
                                console.log(e);
                                setError(e?.response?.data?.errors);
                                actions.setErrors(e.response.data.errors);
                            })
                            .finally(() => {
                                actions.setSubmitting(false);
                            });
                    }}
                >
                    {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => {
                        // Keep the latest values available to the out-of-tree supplier-request modal (IB-610).
                        latestValuesRef.current = values;
                        setFieldValueRef.current = setFieldValue;

                        const contractsOptions = useMemo(() => {
                            const supplier = suppliers.find((sup) => sup.id == values.supplier_id);

                            let options = [{ value: '', label: $t('New Contract') }];

                            options = options.concat(
                                supplier?.supplier_contract.map((contract) => ({
                                    value: contract.id,
                                    label: contract.contract_reference,
                                    tooltip: {
                                        supplierName: supplier.name,
                                        department: contract.departments_names ? contract.departments_names.join(', ') : '-',
                                        contractValue: contract.cashflow.total,
                                        actuals: contract.actual_spend,
                                        variance: (contract.cashflow.total ?? 0) - contract.actual_spend,
                                    },
                                })) || [],
                            );

                            return options;
                        }, [values.supplier_id, suppliers]);

                        return (
                            <div>
                                {/* Overview section */}
                                <div className="card front bg-white border-0 rounded-15px shadow">
                                    <div className="card-body p-3 m-3">
                                        <div className="form-row my-4">
                                            <div className="col col-md-6 d-flex">
                                                <h4 className={'text-blue'}>{$t('Overview')}</h4>
                                            </div>
                                            <div className="col col-md-3 d-flex">
                                                <span className="font-weight-600 mr-3">{$t('Requestor Name')}:</span>
                                                <span>
                                                    {user.first_name || ''} {user.last_name || ''}
                                                </span>
                                            </div>
                                            <div className="col col-md-3 'mr-3'">
                                                <span className="font-weight-600 mr-3">{$t('Submission Date')}:</span>
                                                <span>{submissionDate}</span>
                                            </div>
                                        </div>
                                        <div className="form-row my-4">
                                            <div className="col-md-12 form-group">
                                                <label htmlFor="owner_id">
                                                    {$t('Budget Owner Name')} <span className="text-danger">*</span>
                                                </label>
                                                <UserSelectorInput
                                                    selectedId={values.owner_id}
                                                    onChange={(value) => setFieldValue('owner_id', value)}
                                                    fetchUrl={listUsersUrl}
                                                />
                                                {errors.owner_id && touched.owner_id && <div className="text-danger">{errors.owner_id}</div>}
                                            </div>
                                            <div className="col-md-12 form-group">
                                                <label htmlFor="expenditureDescription">{$t('Expenditure description')}</label>
                                                <Field
                                                    as={'textarea'}
                                                    type="text"
                                                    className={
                                                        errors.description && touched.description
                                                            ? 'form-control is-invalid border border-danger'
                                                            : 'form-control'
                                                    }
                                                    name="description"
                                                    placeholder={$t('Please provide an overview of the expenditure you are seeking approval for here')}
                                                />
                                                {errors.description && touched.description && <div className="text-danger">{errors.description}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Financials section */}
                                <div className="card front bg-white border-0 rounded-15px shadow my-4" ref={financialRef}>
                                    <div className="card-body m-3">
                                        <h4 className={'text-blue'}>{$t('Financials')}</h4>
                                        {financialStep === 1 && (
                                            <PurchaseOrdersCreateUncommittedExpenditureCreate
                                                uncommittedExpendituresCreateUrl={uncommittedExpendituresCreateUrl}
                                                uncommittedExpendituresDetailsUrl={uncommittedExpendituresDetailsUrl}
                                                uncommittedExpendituresStoreUrl={uncommittedExpendituresStoreUrl}
                                                committedExpendituresCreateUrl={committedExpendituresCreateUrl}
                                                committedExpenditures={committedExpenditures}
                                                vikEnabled={props.vikEnabled}
                                                setCommittedExpenditures={setCommittedExpenditures}
                                                setUncommittedExpenditureGroup={setUncommittedExpenditureGroup}
                                                nextStep={changeFinancialStep}
                                                setUncommittedTotal={setUncommittedTotal}
                                                // Reuse the existing draft group when re-selecting so resuming + editing
                                                // never spawns a duplicate group (IB-610).
                                                uncommittedExpenditureGroupId={uncommittedExpenditureGroup ? uncommittedExpenditureGroup.id : null}
                                            />
                                        )}
                                        {financialStep === 2 && !!uncommittedExpenditureGroup && (
                                            <>
                                                <PurchaseOrdersCreateCommittedExpendituresCreate
                                                    committedExpendituresIndexUrl={committedExpendituresIndexUrl}
                                                    committedExpendituresCreateUrl={committedExpendituresCreateUrl}
                                                    committedExpendituresStoreUrl={committedExpendituresStoreUrl}
                                                    uncommittedExpendituresCreateUrl={uncommittedExpendituresCreateUrl}
                                                    uncommittedExpendituresDetailsUrl={uncommittedExpendituresDetailsUrl}
                                                    uncommittedExpendituresUpdateUrl={uncommittedExpendituresUpdateUrl}
                                                    expenseFundApprovalsUpdateUrl={expenseFundApprovalsUpdateUrl}
                                                    suppliersIndexUrl={suppliersIndexUrl}
                                                    uncommittedExpenditureGroupId={uncommittedExpenditureGroup.id}
                                                    uncommittedExpenditureId={expId}
                                                    vikEnabled={props.vikEnabled}
                                                    setExpId={setExpId}
                                                    nextStep={changeFinancialStep}
                                                    setUncommittedExpenditureGroup={setUncommittedExpenditureGroup}
                                                    setLineItems={setLineItems}
                                                    setMaxQuantities={setMaxQuantities}
                                                    setCommittedExpenditures={setCommittedExpenditures}
                                                />
                                            </>
                                        )}
                                        {financialStep === 3 && (
                                            <>
                                                <ProcurementExpensesDetailedTable
                                                    uncommittedExpenditures={lineItems}
                                                    maxQuantities={maxQuantities}
                                                    varianceExists={variance !== 0}
                                                    variance={variance}
                                                    total={total}
                                                ></ProcurementExpensesDetailedTable>

                                                <div className="p-3 float-right">
                                                    <Button variant="danger" onClick={() => changeFinancialStep(2)}>
                                                        {$t('Cancel')}
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="card front bg-white border-0 rounded-15px shadow my-4">
                                    <div className="card-body m-3">
                                        <MilestonesEdit
                                            key={milestonesEditKey}
                                            getMilestonesUrl={props.getExpenseMilestonesUrl}
                                            minDate={props.minDate}
                                            maxDate={props.maxDate}
                                            onValidityChange={handleValidityChange}
                                            onSectionPresent={handleSectionPresent}
                                            onAutosaveStateChange={handleMilestoneSavingChange}
                                            submitButtonText={$t('Confirm')}
                                            showCancelButton={false}
                                            parentSection="direct-commit"
                                            totalValue={uncommittedTotal}
                                            isSliderAllowed={false}
                                            allowConfirm={financialStep === 3}
                                            preserveDraftMilestones={true}
                                        />
                                    </div>
                                </div>

                                {/* Overview of Procurement section */}
                                <div className="card front bg-white border-0 rounded-15px shadow my-4">
                                    <div className="card-body m-3">
                                        <h5 className="table__header">{$t('Overview of Procurement')}</h5>
                                        <div className="form-row my-4">
                                            <div className="col form-group">
                                                <label htmlFor="overview">{$t('Overview of Procurement Process')}</label>
                                                <Field
                                                    as={'textarea'}
                                                    type="text"
                                                    className={
                                                        errors.overview && touched.overview ? 'form-control is-invalid border border-danger' : 'form-control'
                                                    }
                                                    name="overview"
                                                    placeholder={$t('Please provide an overview of the procurement process that has occurred here')}
                                                />
                                                {errors.overview && touched.overview && <div className="text-danger">{errors.overview}</div>}
                                            </div>
                                        </div>
                                        <div className="form-row my-4">
                                            <div className="col form-group">
                                                <label htmlFor="procurement_policy_id">
                                                    {$t('Procurement method')} <span className="text-danger">*</span>
                                                </label>

                                                <div className={'tw-flex tw-flex-row tw-items-center tw-gap-x-1'}>
                                                    <Field
                                                        as={'select'}
                                                        className={
                                                            errors.procurement_policy_id && touched.procurement_policy_id
                                                                ? 'select-input is-invalid border border-danger'
                                                                : 'select-input'
                                                        }
                                                        name="procurement_policy_id"
                                                    >
                                                        {!values.procurement_policy_id && <option value={''}>{$t('Please select')}</option>}
                                                        {procurementPolicies.map((policy) => {
                                                            return (
                                                                <option value={policy.id} key={generateKey()}>
                                                                    {policy.method}
                                                                </option>
                                                            );
                                                        })}
                                                    </Field>
                                                    <InformationCircleIcon
                                                        className="tw-mt-0 tw-mb-0 tw-p-0 tw-cursor-pointer"
                                                        onClick={() => showPurchaseRequisitionProcurementMethodModal()}
                                                    />
                                                </div>

                                                {errors.procurement_policy_id && touched.procurement_policy_id && (
                                                    <div className="text-danger">{errors.procurement_policy_id}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-row my-4">
                                            <div className="col form-group">
                                                <label htmlFor="responses">
                                                    {$t('If fewer than required number of responses / quotes received, please explain why')}
                                                </label>
                                                <Field
                                                    as={'textarea'}
                                                    type="text"
                                                    className={
                                                        errors.responses && touched.responses ? 'form-control is-invalid border border-danger' : 'form-control'
                                                    }
                                                    name="responses"
                                                    placeholder=""
                                                />
                                                {errors.responses && touched.responses && <div className="text-danger">{errors.responses}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Supplier Information section */}
                                <div className="card front bg-white border-0 rounded-15px shadow my-4">
                                    <div className="card-body m-3">
                                        <h5 className="table__header tw-mb-4">{$t('Supplier Information')}</h5>

                                        <div className="tw-space-y-4">
                                            <div className="tw-flex tw-items-center tw-gap-x-2">
                                                <div className="tw-grow tw-flex tw-flex-col tw-space-y-1.5">
                                                    <label htmlFor="supplier_id" className="text-input__label">
                                                        {$t('Recommended Supplier')} <span className="tw-text-red-500 tw-text-sm">*</span>
                                                    </label>
                                                    <SelectField
                                                        className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                                                        options={suppliersOptions}
                                                        value={values.supplier_id}
                                                        backgroundColor="#e4e4e480"
                                                        placeholder={$t('Please select')}
                                                        isDisabled={resolvingRequest}
                                                        onChange={(supplier) => {
                                                            const supplierId = supplier?.value || '';
                                                            setFieldValue('supplier_id', supplierId);
                                                            setFieldValue('contract_id', '');
                                                            // Picking an existing supplier while awaiting supersedes the open request (IB-610 #1).
                                                            if (supplierId && supplierRequest) {
                                                                handleWithdrawForSupplier(supplierId);
                                                            }
                                                        }}
                                                    />
                                                    {touched.supplier_id && errors.supplier_id && (
                                                        <div className="tw-text-red-500 tw-text-sm">{errors.supplier_id}</div>
                                                    )}
                                                </div>

                                                {!props.integrationConfigured && (
                                                    <div className="tw-self-end">
                                                        <Button
                                                            variant="success"
                                                            className="!tw-h-9"
                                                            onClick={() => {
                                                                setCreateNewSupplier(true);
                                                            }}
                                                        >
                                                            <PlusCircleIcon className="tw-w-3.5 tw-h-3.5 tw-mr-2" />
                                                            {$t('Add new Supplier')}
                                                        </Button>
                                                    </div>
                                                )}
                                                {/* ERP-connected events request the supplier from finance instead of creating it locally (IB-610).
                                                    Gated on supplier sync being ENABLED for the event, not merely configured (I6).
                                                    Coerce to boolean: the blade injects this as 0/1, and `{0 && …}` would leak a
                                                    literal "0" into the DOM next to the Add-supplier button on non-ERP events. */}
                                                {!!props.syncSuppliersEnabled && (
                                                    <div className="tw-self-end">
                                                        <Tooltip
                                                            content={
                                                                !hasRecipients
                                                                    ? $t('Add supplier request recipients in Commitment Settings first.')
                                                                    : !uncommittedExpenditureGroup
                                                                      ? $t('Select at least one cost line before requesting a supplier.')
                                                                      : ''
                                                            }
                                                        >
                                                            <Button
                                                                variant="success"
                                                                className="!tw-h-9"
                                                                disabled={!hasRecipients || !uncommittedExpenditureGroup || !!supplierRequest}
                                                                onClick={() => requestSupplierModalElement && requestSupplierModalElement.show()}
                                                            >
                                                                <PlusCircleIcon className="tw-w-3.5 tw-h-3.5 tw-mr-2" />
                                                                {$t('Request new supplier to be added')}
                                                            </Button>
                                                        </Tooltip>
                                                    </div>
                                                )}
                                            </div>

                                            {supplierRequest && (
                                                <div className="tw-rounded-lg tw-border tw-border-amber-400 tw-bg-amber-50 tw-p-3 tw-text-sm tw-text-amber-800 tw-space-y-1">
                                                    <div>
                                                        {$t(
                                                            "Awaiting supplier — {{name}} requested {{date}}. You'll be notified once it's available in the ERP.",
                                                            {
                                                                name: supplierRequest.requested_name,
                                                                date: moment(supplierRequest.created_at).format('DD/MM/YYYY'),
                                                            },
                                                        )}
                                                    </div>
                                                    {/* Finding #1 — let the requestor self-serve if the supplier already exists. */}
                                                    <div className="tw-text-xs tw-text-amber-700">
                                                        {$t('Already in the list? Select the supplier above to use it and cancel this request.')}
                                                    </div>
                                                </div>
                                            )}

                                            {values.supplier_id && (
                                                <div className="tw-grow tw-flex tw-flex-col tw-space-y-1.5">
                                                    <label htmlFor="contract_id" className="text-input__label">
                                                        {$t('Contract Reference')}
                                                    </label>
                                                    <SelectField
                                                        className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                                                        options={contractsOptions}
                                                        value={values.contract_id}
                                                        formatOptionLabel={formatContractOptionLabel}
                                                        backgroundColor="#e4e4e480"
                                                        placeholder={$t('Please select')}
                                                        onChange={(contract) => {
                                                            setFieldValue('contract_id', contract?.value || '');
                                                        }}
                                                    />
                                                    {touched.contract_id && errors.contract_id && (
                                                        <div className="tw-text-red-500 tw-text-sm">{errors.contract_id}</div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="tw-grid tw-grid-cols-2 tw-gap-x-2">
                                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                    <label className="text-input__label">{$t('Contract Start Date')}</label>
                                                    <DatePickerField
                                                        name="contract_started_at"
                                                        valueFormat="yyyy-MM-dd"
                                                        dateFormat="yyyy-MM-dd"
                                                        className={classNames(
                                                            'text-input !tw-bg-grey/50',
                                                            errors.contract_started_at && touched.contract_started_at
                                                                ? 'tw-border tw-border-red-500'
                                                                : 'tw-border-0',
                                                        )}
                                                        disabled={isSubmitting}
                                                        value={values.contract_started_at}
                                                        onChange={setFieldValue}
                                                        wrapped
                                                    />
                                                    {errors.contract_started_at && touched.contract_started_at && (
                                                        <div className="tw-text-red-500 tw-text-sm">{errors.contract_started_at}</div>
                                                    )}
                                                </div>
                                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                    <label className="text-input__label">{$t('Contract Ended Date')}</label>
                                                    <DatePickerField
                                                        name="contract_ended_at"
                                                        valueFormat="yyyy-MM-dd"
                                                        dateFormat="yyyy-MM-dd"
                                                        className={classNames(
                                                            'text-input !tw-bg-grey/50',
                                                            errors.contract_ended_at && touched.contract_ended_at
                                                                ? 'tw-border tw-border-red-500'
                                                                : 'tw-border-0',
                                                        )}
                                                        disabled={isSubmitting}
                                                        value={values.contract_ended_at}
                                                        onChange={setFieldValue}
                                                        wrapped
                                                    />
                                                    {errors.contract_ended_at && touched.contract_ended_at && (
                                                        <div className="tw-text-red-500 tw-text-sm">{errors.contract_ended_at}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="form-row my-4">
                                                <div className="col-md-6 form-group">
                                                    <CheckboxFormField
                                                        name="contract_signed"
                                                        id="contract_signed"
                                                        type="checkbox"
                                                        label={$t('Contract Signed')}
                                                    />
                                                    {errors.contract_signed && touched.contract_signed && (
                                                        <div className="text-danger">{errors.contract_signed}</div>
                                                    )}
                                                </div>
                                                <div className="col-md-6 form-group">
                                                    <CheckboxFormField
                                                        name="contract_closed"
                                                        id="contract_closed"
                                                        type="checkbox"
                                                        label={$t('Contract Closed')}
                                                    />
                                                    {errors.contract_closed && touched.contract_closed && (
                                                        <div className="text-danger">{errors.contract_closed}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Other section */}
                                <div className="card front bg-white border-0 rounded-15px shadow my-4">
                                    <div className="card-body m-3">
                                        <h5 className="table__header">{$t('Other')}</h5>
                                        <div className="form-row my-4">
                                            <div className="col form-group">
                                                <label htmlFor="conflicts">{$t('Conflicts of Interest')}</label>
                                                <Field
                                                    as={'textarea'}
                                                    type="text"
                                                    className={
                                                        errors.conflicts && touched.conflicts ? 'form-control is-invalid border border-danger' : 'form-control'
                                                    }
                                                    name="conflicts"
                                                    placeholder={$t('Please identify any conflicts of interest here including mitigating actions here')}
                                                />
                                                {errors.conflicts && touched.conflicts && <div className="text-danger">{errors.conflicts}</div>}
                                            </div>
                                        </div>

                                        <div className="form-row my-4">
                                            <div className="col form-group">
                                                <label htmlFor="risks">{$t('Risks')}</label>
                                                <Field
                                                    as={'textarea'}
                                                    type="text"
                                                    className={errors.risks && touched.risks ? 'form-control is-invalid border border-danger' : 'form-control'}
                                                    name="risks"
                                                    placeholder={$t('Please describe any key risks identified with this procurement here')}
                                                />
                                                {errors.risks && touched.risks && <div className="text-danger">{errors.risks}</div>}
                                            </div>
                                        </div>

                                        <div className="form-row my-4">
                                            <div className="col form-group">
                                                <label htmlFor="other">{$t('Other Information')}</label>
                                                <Field
                                                    as={'textarea'}
                                                    type="text"
                                                    className={errors.other && touched.other ? 'form-control is-invalid border border-danger' : 'form-control'}
                                                    name="other"
                                                    placeholder={$t('Please capture any other information relevant to this procurement approval here')}
                                                />
                                                {errors.other && touched.other && <div className="text-danger">{errors.other}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Attachments section */}
                                <div className="card front bg-white border-0 rounded-15px shadow my-4">
                                    <div className="card-body m-3">
                                        <h5 className="table__header">{$t('Attachments')}</h5>
                                        <p className="tw-text-xs tw-text-charcoal-50 tw-mb-2">{$t('Attachments are added when you commit, not on a draft.')}</p>
                                        <div className="form-row my-4">
                                            <div className="col-4">
                                                <div className="form-group">
                                                    <input
                                                        id="purchase-order-attachments"
                                                        type="file"
                                                        name="files[]"
                                                        multiple
                                                        className="file-input"
                                                        placeholder={$t('Select Files')}
                                                        accept={ACCEPTED_ATTACHMENT_FILE_TYPES}
                                                        onChange={changeFilesHandler}
                                                    />
                                                    {errors.files && touched.files && <div className="text-danger">{errors.files}</div>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {!!error && _.isObject(error) && (
                                    <>
                                        {Object.values(error)[0].map((e, i) => {
                                            return <DangerAlert key={i} message={e} />;
                                        })}
                                    </>
                                )}

                                <div className="row">
                                    <div className="col-12 d-flex justify-content-end tw-gap-x-3">
                                        {/* Save draft stays enabled even while a supplier request is open (IB-610). */}
                                        <Button variant="secondary" disabled={isSubmitting} onClick={() => handleSaveDraft(values)}>
                                            {$t('Save draft')}
                                        </Button>
                                        <Button variant="primary" disabled={isSubmitting || submitDisabled || !!supplierRequest} onClick={handleSubmit}>
                                            {$t('Submit for Approval')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                </Formik>
            )}
            <SuppliersCreateModal
                modalId={'create-supplier-modal'}
                modalRef={suppliersCreateModalRef}
                modalElement={suppliersCreateModalElement}
                closeHandler={closeModal}
                successHandler={successHandler}
                suppliersIndexUrl={suppliersIndexUrl}
                suppliersStoreUrl={suppliersStoreUrl}
                suppliersCreateUrl={suppliersCreateUrl}
            />
            <RequestSupplierModal
                modalId={'request-supplier-modal'}
                modalRef={requestSupplierModalRef}
                modalElement={requestSupplierModalElement}
                storeUrl={props.supplierRequestStoreUrl}
                requestableType={'App\\UncommittedExpenditureGroup'}
                requestableId={uncommittedExpenditureGroup ? uncommittedExpenditureGroup.id : null}
                recipients={supplierRequestRecipients}
                suggestionsUrl={props.supplierRequestSuggestionsUrl}
                onSelectExisting={(supplier) => setFieldValueRef.current && setFieldValueRef.current('supplier_id', supplier.id)}
                onBeforeRequest={() => handleSaveDraft(latestValuesRef.current, { silent: true })}
                onRequested={(data) => setSupplierRequest(data)}
            />
            <PurchaseRequisitionProcurementMethodModal
                closeHandler={closePMModal}
                successHandler={successPMHandler}
                cleanStates={cleanConfirmationModalStates}
                allowAction={true}
                modalId={'pr-method-list'}
                modalRef={purchaseRequisitionProcurementMethodModalRef}
                modalElement={purchaseRequisitionProcurementMethodModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                title={$t('Procurement Policies')}
                cancelButtonText={$t('Close')}
                policies={procurementPolicies}
            />
        </div>
    );
}
